import { useEffect, useRef } from 'react'
import { isAppLocked, useIsAppLocked } from '../../stores/app-lock-store'
import { isNativeApp } from '../../utils/platform'
import { isTextEntryElement } from '../../utils/text-entry'

/**
 * Single owner of the soft-lock's DOM side-effects on the app chrome.
 *
 * The lock blocks the routed app three ways while active: `inert` (taps +
 * focus + a11y), `aria-hidden` (screen readers), and `pointer-events-none`
 * (hit-testing, incl. touch scrolling). Builds 91/57 shipped these scattered
 * across the App wrapper and the sheet; when an unlock path skipped a
 * re-render the app was left visible but completely dead (no scrolling, nav
 * unusable, only >z-50 overlays tappable). Everything is now derived from
 * {@link appChromeGateProps} — apply and release enumerate the SAME artifact
 * list — and {@link useSoftLockDomRelease} adds a belt-and-braces sweep on
 * every locked→unlocked store transition so no future refactor can strand the
 * app half-dead again.
 */

/** Marker attribute so the release sweep can find the gated chrome root(s). */
export const APP_CHROME_ROOT_ATTR = 'data-app-chrome-root'

const POINTER_LOCK_CLASS = 'pointer-events-none'

/**
 * The chrome root sits between `.native-viewport` (a flex column locked to
 * the WebView viewport on native) and `.app-shell-root` (`height: 100%` on
 * native). It MUST stretch to fill that column: as a default flex item it
 * collapsed to content height, so on devices the shell was content-sized —
 * short pages rendered the bottom nav mid-screen, long pages pushed it below
 * the viewport ("nav bar gone"), no scroll container had a bounded height
 * ("can't scroll anywhere"), and only position:fixed overlays (call UI,
 * modals) stayed usable. Web never showed it because the shell keeps
 * `h-dvh` there — the native `height: 100%` chain is what broke.
 */
const CHROME_LAYOUT_CLASSES = 'flex min-h-0 w-full max-w-full flex-1 flex-col'

/**
 * `keyboard-open` on <html> CSS-locks document scrolling and (via AppShell)
 * unmounts the mobile bottom nav. It is owned by use-keyboard-visible, but a
 * missed native keyboardWillHide (WebView paused during the OS biometric /
 * credential activity) can strand it — swept here as part of unlock release.
 */
const KEYBOARD_OPEN_CLASS = 'keyboard-open'

/**
 * Second sweep: catches artifacts re-stranded by exit animations / unmount
 * effects that settle just after the unlock commit (overlay fade-outs run
 * 200-300ms).
 */
const LATE_SWEEP_DELAY_MS = 400

type AppChromeGateProps = {
  inert: boolean | undefined
  'aria-hidden': true | undefined
  className: string
  'data-app-chrome-root': ''
}

/**
 * The one place that knows how the soft-lock blocks the app chrome. App.tsx
 * spreads this onto the route container; the release sweep clears exactly the
 * same artifacts, so the two can never drift apart. The layout classes are
 * unconditional — only the pointer lock toggles with the lock state.
 */
export function appChromeGateProps(blocked: boolean): AppChromeGateProps {
  return {
    inert: blocked ? true : undefined,
    'aria-hidden': blocked || undefined,
    className: blocked ? `${CHROME_LAYOUT_CLASSES} ${POINTER_LOCK_CLASS}` : CHROME_LAYOUT_CLASSES,
    'data-app-chrome-root': '',
  }
}

/**
 * Idempotent release of every DOM artifact the soft-lock can leave behind.
 * Never runs while genuinely locked (would weaken the modal security from
 * #295). Returns the artifacts that were actually found lingering — a
 * non-empty result means some unlock path skipped its own cleanup.
 */
export function releaseSoftLockDomArtifacts(): string[] {
  if (typeof document === 'undefined') return []
  if (isAppLocked()) return []
  const found: string[] = []

  for (const root of Array.from(document.querySelectorAll(`[${APP_CHROME_ROOT_ATTR}]`))) {
    if (!(root instanceof HTMLElement)) continue
    if (root.hasAttribute('inert')) {
      root.removeAttribute('inert')
      found.push('inert')
    }
    if (root.getAttribute('aria-hidden') === 'true') {
      root.removeAttribute('aria-hidden')
      found.push('aria-hidden')
    }
    if (root.classList.contains(POINTER_LOCK_CLASS)) {
      root.classList.remove(POINTER_LOCK_CLASS)
      found.push(POINTER_LOCK_CLASS)
    }
  }

  // A real keyboard may legitimately be open (user already typing) — only a
  // keyboard-open with no focused text entry is a stranded scroll/nav lock.
  const html = document.documentElement
  if (html.classList.contains(KEYBOARD_OPEN_CLASS) && !isTextEntryElement(document.activeElement)) {
    html.classList.remove(KEYBOARD_OPEN_CLASS)
    found.push(KEYBOARD_OPEN_CLASS)
  }

  return found
}

function sweep(phase: 'unlock' | 'late'): void {
  if (isAppLocked()) return
  const found = releaseSoftLockDomArtifacts()
  if (found.length > 0 && process.env.NODE_ENV !== 'production') {
    // Loud on purpose: a lingering artifact means an unlock path skipped its
    // own teardown — the sweep healed the app, but the path needs fixing.
    console.error(
      `[soft-lock] ${phase} release found DOM artifacts a teardown path missed:`,
      found,
    )
  }
}

/**
 * Belt-and-braces release, mounted once in App: on every locked→unlocked
 * store transition, sweep lingering artifacts (immediately after React's own
 * commit, and once more after exit animations settle) and re-sync the native
 * viewport. The normal path finds nothing — this only catches strandings.
 */
export function useSoftLockDomRelease(): void {
  const locked = useIsAppLocked()
  const prevRef = useRef(locked)

  useEffect(() => {
    const wasLocked = prevRef.current
    prevRef.current = locked
    if (locked || !wasLocked) return

    // This effect runs after React committed the unlock render, so anything
    // the sweep finds was genuinely stranded by a missed teardown path.
    sweep('unlock')

    if (isNativeApp()) {
      // The OS biometric/credential round-trip can leave the WebView with a
      // stale scroll offset or keyboard inset (the "nav bar gone / can't
      // scroll" state). Same nudge use-native-shell applies on keyboardDidHide:
      // snap the document to the top and force a viewport re-measure.
      window.scrollTo(0, 0)
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
      window.dispatchEvent(new Event('resize'))
    }

    const late = window.setTimeout(() => sweep('late'), LATE_SWEEP_DELAY_MS)
    return () => window.clearTimeout(late)
  }, [locked])
}
